import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";
import { OrderRateLimit, SelectedCustomer } from "./types";

export const useOrderRateLimits = (selectedCustomer?: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [orderRateLimits, setOrderRateLimits] = useState<OrderRateLimit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await axios.get<{ order_rate_limits: OrderRateLimit[] }>("/api/getAllOrderRateLimits", {
        params: { customer_id: customerId },
      });
      setOrderRateLimits(res.data.order_rate_limits);
    } catch (error) {
      generalErrorAlert(error, "Error retrieving Checkout Limitations, please refresh.");
    }
  }, [customerId]);

  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      await load();
      setIsLoading(false);
    };
    initialLoad();
  }, [load]);

  // Mirrors addOrderRateLimitController's saveOrderRateLimit().
  const saveOrderRateLimit = useCallback(
    async (orderRateLimit: OrderRateLimit) => {
      if (!customerId) return false;
      try {
        await axios.post("/api/saveOrderRateLimit/", {
          customer_id: customerId,
          order_rate_limit: orderRateLimit,
        });
        notification.success({ message: "Order Throttling Updated!" });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Error saving. Please refresh and try again.");
        return false;
      }
    },
    [customerId, load]
  );

  // Mirrors settingsController's deleteSelectedOrderRateLimits().
  const deleteOrderRateLimits = useCallback(
    async (orderRateLimitIds: string[]) => {
      try {
        await axios.post("/api/deleteOrderRateLimits", { order_rate_limit_ids_to_delete: orderRateLimitIds });
        notification.success({ message: "Selected throttling settings have been deleted." });
        await load();
        return true;
      } catch (error) {
        generalErrorAlert(error, "Error deleting throttling settings.");
        return false;
      }
    },
    [load]
  );

  return { orderRateLimits, isLoading, saveOrderRateLimit, deleteOrderRateLimits };
};
